import { useCallback, useEffect, useRef, useState } from 'react';
import type { MRT_SortingState } from 'mantine-react-table';

type SortingUpdater =
  | MRT_SortingState
  | ((currentSorting: MRT_SortingState) => MRT_SortingState);

interface UseCustomColumnSortingOptions {
  initialSorting?: MRT_SortingState;
  onSortingChange?: (sorting: MRT_SortingState) => void;
}

interface UseCustomColumnSortingResult {
  sorting: MRT_SortingState;
  onSortingChange: (updater: SortingUpdater) => void;
}

export const useCustomColumnSorting = ({
  initialSorting = [],
  onSortingChange,
}: UseCustomColumnSortingOptions = {}): UseCustomColumnSortingResult => {
  const [sorting, setSorting] = useState<MRT_SortingState>(initialSorting);
  const sortingRef = useRef(sorting);

  useEffect(() => {
    sortingRef.current = sorting;
  }, [sorting]);

  const handleSortingChange = useCallback(
    (updater: SortingUpdater) => {
      const nextSorting =
        typeof updater === 'function' ? updater(sortingRef.current) : updater;

      sortingRef.current = nextSorting;
      setSorting(nextSorting);
      onSortingChange?.(nextSorting);
    },
    [onSortingChange]
  );

  return { sorting, onSortingChange: handleSortingChange };
};
